import { DataTable } from '@/components/common/DataTable';
import { ErrorBanner } from '@/components/common/ErrorBanner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAppDispatch } from '@/hooks';
import { usePlayer } from '@/hooks/redux';
import { getPlayerById, updatePlayerStatus } from '@/store/slices/playerSlice';
import { DepositHistory, GameHistory, Player, WithdrawalHistory } from '@/types';
import { ColumnDef } from '@tanstack/react-table';
import {
  ArrowLeft,
  Ban,
  Camera,
  CircleOff,
  Clock,
  Edit,
  Mail,
  MoreHorizontal,
  Phone,
  Save,
  ThumbsDownIcon,
  ThumbsUpIcon,
  Timer,
  TimerIcon,
  Trash,
  UserRound,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export const depositColumns: ColumnDef<DepositHistory>[] = [
  {
    accessorKey: 'id',
    header: 'ID',
  },
  {
    accessorKey: 'username',
    header: 'User',
    cell: ({ row }) => <span>{row.original.username}</span>,
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    cell: ({ row }) => <span>₹{row.original.amount.toLocaleString()}</span>,
  },
  {
    accessorKey: 'paymentMethod',
    header: 'Method',
  },
  {
    accessorKey: 'transactionId',
    header: 'Transaction ID',
  },
  {
    accessorKey: 'referenceId',
    header: 'Reference ID',
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status;
      const color =
        status === 'success'
          ? 'text-green-600'
          : status === 'pending'
            ? 'text-yellow-500'
            : 'text-red-600';
      return <span className={color}>{status}</span>;
    },
  },
  {
    accessorKey: 'createdAt',
    header: 'Date',
    cell: ({ row }) => new Date(row.original.createdAt).toLocaleString('en-IN'),
  },
  {
    accessorKey: 'action',
    header: 'Action',
    cell: ({ row }) => {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <MoreHorizontal />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem>View</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];

export const depositData: DepositHistory[] = [
  {
    id: 'DEP1001',
    userId: 'USR101',
    username: 'Rahul Sharma',
    amount: 500,
    status: 'success',
    paymentMethod: 'upi',
    transactionId: 'TXNUPI998811',
    referenceId: 'UPIREF12345',
    createdAt: '2025-02-20T10:15:00Z',
  },
  {
    id: 'DEP1002',
    userId: 'USR102',
    username: 'Amit Verma',
    amount: 2000,
    status: 'pending',
    paymentMethod: 'bank',
    transactionId: 'BANKTX909090',
    referenceId: '',
    createdAt: '2025-02-20T11:40:00Z',
  },
  {
    id: 'DEP1003',
    userId: 'USR103',
    username: 'Sunil Gupta',
    amount: 1500,
    status: 'failed',
    paymentMethod: 'upi',
    transactionId: 'UPI00007654',
    referenceId: 'UPIREF87965',
    createdAt: '2025-02-20T12:10:00Z',
  },
  {
    id: 'DEP1004',
    userId: 'USR104',
    username: 'Arjun Singh',
    amount: 300,
    status: 'success',
    paymentMethod: 'wallet',
    transactionId: 'WAL88776655',
    referenceId: '',
    createdAt: '2025-02-21T09:05:00Z',
  },
  {
    id: 'DEP1005',
    userId: 'USR105',
    username: 'Rohit Kumar',
    amount: 1000,
    status: 'success',
    paymentMethod: 'upi',
    transactionId: 'TXNUPI007812',
    referenceId: 'UPIREF88990',
    createdAt: '2025-02-21T10:30:00Z',
  },
  {
    id: 'DEP1006',
    userId: 'USR106',
    username: 'Deepak Yadav',
    amount: 750,
    status: 'pending',
    paymentMethod: 'bank',
    transactionId: 'BANKTX442211',
    referenceId: '',
    createdAt: '2025-02-21T14:15:00Z',
  },
  {
    id: 'DEP1007',
    userId: 'USR107',
    username: 'Manish Patel',
    amount: 999,
    status: 'failed',
    paymentMethod: 'upi',
    transactionId: 'TXN00449911',
    referenceId: 'UPIREF55773',
    createdAt: '2025-02-22T08:45:00Z',
  },
  {
    id: 'DEP1008',
    userId: 'USR108',
    username: 'Karan Singh',
    amount: 2500,
    status: 'success',
    paymentMethod: 'card',
    transactionId: 'CRDT99811122',
    referenceId: '',
    createdAt: '2025-02-22T09:18:00Z',
  },
  {
    id: 'DEP1009',
    userId: 'USR109',
    username: 'Sahil Jain',
    amount: 600,
    status: 'success',
    paymentMethod: 'upi',
    transactionId: 'UPI00211387',
    referenceId: 'UPIREF44221',
    createdAt: '2025-02-22T10:05:00Z',
  },
  {
    id: 'DEP1010',
    userId: 'USR110',
    username: 'Vikas Pawar',
    amount: 1800,
    status: 'pending',
    paymentMethod: 'bank',
    transactionId: 'BANK00771234',
    referenceId: '',
    createdAt: '2025-02-22T12:00:00Z',
  },
];

export const withdrawalColumns: ColumnDef<WithdrawalHistory>[] = [
  {
    accessorKey: 'id',
    header: 'ID',
  },
  {
    accessorKey: 'username',
    header: 'User',
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    cell: ({ row }) => <span>₹{row.original.amount.toLocaleString()}</span>,
  },
  {
    accessorKey: 'payoutMethod',
    header: 'Method',
  },
  {
    accessorKey: 'accountNumber',
    header: 'Account / UPI',
  },
  {
    accessorKey: 'transactionId',
    header: 'Payout Txn ID',
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status;
      const color =
        status === 'approved'
          ? 'text-green-600'
          : status === 'pending'
            ? 'text-yellow-500'
            : status === 'processing'
              ? 'text-blue-600'
              : 'text-red-600';

      return <span className={color}>{status}</span>;
    },
  },
  {
    accessorKey: 'createdAt',
    header: 'Requested At',
    cell: ({ row }) => new Date(row.original.createdAt).toLocaleString('en-IN'),
  },
  {
    accessorKey: 'action',
    header: 'Action',
    cell: ({ row }) => {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <MoreHorizontal />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem>View</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];

export const withdrawalData: WithdrawalHistory[] = [
  {
    id: 'WDR2001',
    userId: 'USR101',
    username: 'Rahul Sharma',
    amount: 700,
    status: 'approved',
    payoutMethod: 'upi',
    accountNumber: 'rahul@upi',
    transactionId: 'WUPI887766',
    remark: 'Processed successfully',
    createdAt: '2025-02-20T08:30:00Z',
  },
  {
    id: 'WDR2002',
    userId: 'USR102',
    username: 'Amit Verma',
    amount: 2500,
    status: 'pending',
    payoutMethod: 'bank',
    accountNumber: 'XXXXXX1234',
    ifscCode: 'HDFC0000111',
    transactionId: '',
    remark: '',
    createdAt: '2025-02-20T09:00:00Z',
  },
  {
    id: 'WDR2003',
    userId: 'USR103',
    username: 'Sunil Gupta',
    amount: 500,
    status: 'processing',
    payoutMethod: 'upi',
    accountNumber: 'sunil@okaxis',
    transactionId: '',
    remark: 'Queued',
    createdAt: '2025-02-20T11:20:00Z',
  },
  {
    id: 'WDR2004',
    userId: 'USR104',
    username: 'Arjun Singh',
    amount: 1500,
    status: 'rejected',
    payoutMethod: 'bank',
    accountNumber: 'XXXXXX8899',
    ifscCode: 'SBIN0001212',
    transactionId: '',
    remark: 'Invalid account number',
    createdAt: '2025-02-20T13:45:00Z',
  },
  {
    id: 'WDR2005',
    userId: 'USR105',
    username: 'Rohit Kumar',
    amount: 300,
    status: 'approved',
    payoutMethod: 'wallet',
    accountNumber: 'WAL99887766',
    transactionId: 'WALTX112233',
    remark: 'Done',
    createdAt: '2025-02-21T07:15:00Z',
  },
  {
    id: 'WDR2006',
    userId: 'USR106',
    username: 'Deepak Yadav',
    amount: 1800,
    status: 'pending',
    payoutMethod: 'upi',
    accountNumber: 'deepak@ybl',
    transactionId: '',
    remark: '',
    createdAt: '2025-02-21T10:10:00Z',
  },
  {
    id: 'WDR2007',
    userId: 'USR107',
    username: 'Manish Patel',
    amount: 1200,
    status: 'approved',
    payoutMethod: 'bank',
    accountNumber: 'XXXXXX4410',
    ifscCode: 'ICIC0000221',
    transactionId: 'BNK11221144',
    remark: 'Success',
    createdAt: '2025-02-21T13:50:00Z',
  },
  {
    id: 'WDR2008',
    userId: 'USR108',
    username: 'Karan Singh',
    amount: 999,
    status: 'failed',
    payoutMethod: 'upi',
    accountNumber: 'karan@upi',
    transactionId: '',
    remark: 'UPI server error',
    createdAt: '2025-02-22T06:45:00Z',
  },
  {
    id: 'WDR2009',
    userId: 'USR109',
    username: 'Sahil Jain',
    amount: 4000,
    status: 'approved',
    payoutMethod: 'bank',
    accountNumber: 'XXXXXX5566',
    ifscCode: 'KKBK0002211',
    transactionId: 'BNK778899',
    remark: 'Processed',
    createdAt: '2025-02-22T07:55:00Z',
  },
  {
    id: 'WDR2010',
    userId: 'USR110',
    username: 'Vikas Pawar',
    amount: 600,
    status: 'processing',
    payoutMethod: 'wallet',
    accountNumber: 'WAL001199',
    transactionId: '',
    remark: '',
    createdAt: '2025-02-22T08:20:00Z',
  },
];

export const gameHistoryData: GameHistory[] = [
  {
    roundId: 'R1001',
    userId: 'USR101',
    username: 'Rahul Sharma',
    tableNumber: 1231,
    betType: 'dragon',
    result: 'tiger',
    amount: 500,
    win: false,
    profit: -500,
    createdAt: '2025-02-22T09:21:00Z',
  },
  {
    roundId: 'R1002',
    userId: 'USR101',
    username: 'Rahul Sharma',
    tableNumber: 1231,
    betType: 'tiger',
    result: 'tiger',
    amount: 1000,
    win: true,
    profit: 950, // example including commission
    createdAt: '2025-02-22T09:30:00Z',
  },
  {
    roundId: 'R1003',
    userId: 'USR101',
    username: 'Rahul Sharma',
    tableNumber: 1231,
    betType: 'tie',
    result: 'tie',
    amount: 200,
    win: true,
    profit: 1800, // tie often pays 9x
    createdAt: '2025-02-22T09:35:00Z',
  },
  {
    roundId: 'R1004',
    userId: 'USR101',
    username: 'Rahul Sharma',
    tableNumber: 1232,
    betType: 'tiger',
    result: 'dragon',
    amount: 300,
    win: false,
    profit: -300,
    createdAt: '2025-02-22T09:40:00Z',
  },
];

const PlayerProfile = () => {
  const { playerId } = useParams();
  const dispatch = useAppDispatch();
  const { player, players } = usePlayer();
  const navigate = useNavigate();

  useEffect(() => {
    if (playerId) {
      dispatch(getPlayerById(playerId));
    }
  }, [playerId]);

  const handleSelectionChange = (selectedPlayer: Player[]) => {};

  const blockUser = (id: string, status: Player['status']) => {
    dispatch(updatePlayerStatus({ id, status })).unwrap();
  };

  const unBlockUser = (id: string, status: Player['status']) => {
    dispatch(updatePlayerStatus({ id, status })).unwrap();
  };

  if (!player)
    return (
      <div className="w-full flex">
        <span>Loading...</span>
      </div>
    );

  return (
    <div className="space-y-6">
      <Button onClick={() => navigate(-1)} variant="outline" className="w-full sm:w-auto">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Go Back
      </Button>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Profile</h1>
          <p className="text-muted-foreground">Manage your personal information and preferences</p>
        </div>
        <div className="flex items-center gap-2">
          {player.status === 'Active' ? (
            <Button size={'sm'} onClick={() => blockUser(player.id, 'Blocked')} className="gap-1">
              <Ban size={14} /> Block
            </Button>
          ) : (
            <Button
              size={'sm'}
              onClick={() => blockUser(player.id, 'Active')}
              className="gap-1 bg-green-500 hover:bg-green-400"
            >
              <CircleOff size={14} /> Unblock
            </Button>
          )}
          <Button size="sm" variant="destructive">
            <Trash size={18} />
          </Button>
        </div>
      </div>
      <div className="grid gap-6 md:grid-cols-3">
        {/* Profile Card */}
        <Card className="md:col-span-1">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              <div className="relative">
                <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-2xl font-bold">
                  {player?.username?.charAt(0) || 'U'}
                </div>
              </div>

              <div className="text-center space-y-1">
                <h3 className="text-xl font-semibold">{player.username}</h3>
              </div>

              <div className="w-full space-y-3 text-sm">
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <UserRound className="h-4 w-4 " />
                  <span>{player.id}</span>
                </div>
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  <span>{player.email}</span>
                </div>
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  <span>{player.mobile || 'Not provided'}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="w-full space-y-6">
        <Tabs defaultValue="wallet">
          <TabsList className="w-full justify-around">
            <TabsTrigger value="wallet">Wallet Balance</TabsTrigger>
            <TabsTrigger value="deposite">Deposit History</TabsTrigger>
            <TabsTrigger value="withdrawal">Withdrawal History</TabsTrigger>
            <TabsTrigger value="gameHistory">Game History</TabsTrigger>
            {/* <TabsTrigger value='kycDocuments'>KYC Documents (Approve / Reject)</TabsTrigger>
                        <TabsTrigger value='deviceInfo'>Device Info</TabsTrigger> */}
          </TabsList>
          <TabsContent value="wallet">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-semibold">Wallet Overview</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Player’s financial summary & recent activity
                </p>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Balance Box */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="rounded-lg p-4 bg-muted/50">
                    <p className="text-sm text-foreground">Available Balance</p>
                    <p className="text-2xl font-bold">₹{player?.wallet?.amount}</p>
                  </div>

                  <div className="rounded-lg p-4 bg-muted/30">
                    <p className="text-sm text-foreground">Total Deposited</p>
                    <p className="text-xl font-semibold">₹0</p>
                  </div>

                  <div className="rounded-lg p-4 bg-muted/30">
                    <p className="text-sm text-foreground">Total Withdrawn</p>
                    <p className="text-xl font-semibold">₹0</p>
                  </div>
                </div>

                {/* Last Transaction */}
                <div className="p-4 rounded-lg border bg-card">
                  <h4 className="font-semibold mb-2">Last Transaction</h4>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">'No transactions yet'</span>
                  </div>
                </div>

                {/* Actions */}
                {/* <div className="flex gap-3">
                                    <Button variant="default" className="flex-1">
                                        Add Balance
                                    </Button>
                                    <Button variant="destructive" className="flex-1">
                                        Deduct Balance
                                    </Button>
                                </div> */}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="deposite">
            <DataTable
              columns={depositColumns}
              data={depositData}
              // onSelectionChange={handleSelectionChange}
              enableSelection={true}
              enableFilters={true}
              onDelete={() => null}
              pageIndex={0}
              total={0}
              onPageChange={function (page: number): void {
                throw new Error('Function not implemented.');
              }}
              onPageSizeChange={function (size: number): void {
                throw new Error('Function not implemented.');
              }}
            />
          </TabsContent>
          <TabsContent value="withdrawal">
            <DataTable
              columns={withdrawalColumns}
              data={withdrawalData}
              enableSelection={true}
              pageIndex={0}
              total={0}
              onPageChange={function (page: number): void {
                throw new Error('Function not implemented.');
              }}
              onPageSizeChange={function (size: number): void {
                throw new Error('Function not implemented.');
              }}
            />
          </TabsContent>
          <TabsContent value="gameHistory">
            {gameHistoryData.map((game) => (
              <div key={game.roundId} className="flex items-center gap-2">
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  09.21 am
                </span>
                <span>Table number: 1231</span>
                <span>Bet type : Dragon</span>
                <span>Bet amount : 3000</span>
                <span>Game result : Tiger</span>
                <span className="flex items-center gap-1">
                  {game.win ? (
                    <>
                      User Win <ThumbsUpIcon className="h-4 w-4 text-green-500" />
                    </>
                  ) : (
                    <>
                      User lose <ThumbsDownIcon className="h-4 w-4 text-red-500" />
                    </>
                  )}
                </span>
                <span className={`${game.win ? 'text-green-500' : 'text-red-500'}`}>
                  {game.win ? `+$${game.amount}` : `-$${game.amount}`}
                </span>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PlayerProfile;
